// LearnCore Frontend - 主题切换Hook

import { useState, useEffect } from 'react';

export type Theme = 'light' | 'dark' | 'auto';

interface ThemeConfig {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
  systemTheme: 'light' | 'dark';
  toggleTheme: () => void;
}

export function useTheme(): ThemeConfig {
  const [theme, setThemeState] = useState<Theme>(() => {
    // 从localStorage读取保存的主题设置
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('learncore-theme') as Theme;
      if (saved && ['light', 'dark', 'auto'].includes(saved)) {
        return saved;
      }
    }
    return 'auto'; // 默认跟随系统
  });

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // 获取实际应用的主题
  const resolvedTheme: 'light' | 'dark' = theme === 'auto' ? systemTheme : theme;

  // 监听系统主题变化
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // 应用主题到DOM
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const html = document.documentElement;

    // 设置data-theme属性（用于CSS变量）
    root.setAttribute('data-theme', theme);

    // 设置dark类（用于Tailwind CSS）
    if (resolvedTheme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // 添加CSS类到body，方便CSS选择器使用
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${resolvedTheme}`);

    // 保存到localStorage
    localStorage.setItem('learncore-theme', theme);

    // 开发环境调试
    if (import.meta.env.DEV) {
      console.log('🎨 主题切换:', {
        theme,
        resolvedTheme,
        systemTheme,
        htmlClasses: html.className,
        bodyClasses: document.body.className
      });
    }
  }, [theme, resolvedTheme, systemTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('auto');
    } else {
      setTheme('light');
    }
  };

  return {
    theme,
    setTheme,
    resolvedTheme,
    systemTheme,
    toggleTheme
  };
}